/**
 * Writing a purchase order by hand (SCOPE §6, §5.2).
 *
 * The client asked for this in their own words: *"Writing purchase orders:
 * Either pull items from existing inventory or build new. By - grid or sku."*
 * Until now an order could only be born from a vendor file, which covers a
 * season's buy and nothing else — a phone reorder of last season's gloves has
 * no spreadsheet.
 *
 * Pure, for the same reason `receiving-lines.ts` is: these are quantity and
 * money decisions that end up in inventory and on an invoice, and they were
 * otherwise going to be made inline in JSX where nothing can assert them.
 *
 * **There is no "grid or SKU" mode to choose.** §5.2 settled that question for
 * product creation — "does this have sizes?" is answered by typing sizes or not
 * — and the same answer applies here: pick a style, and its colour × size grid
 * is however big it is. A tub of wax is a grid with one cell. Offering two
 * modes would be a decision point that buys nothing (§2.2-2).
 */

import { describeVariant } from '@sitzmark/core';

export interface OrderableVariant {
  readonly variantId: string;
  readonly productId: string;
  readonly productName: string;
  readonly sku: string;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  /** A length or other second axis — part of what is ordered, so part of the line. */
  readonly altSize?: string | null;
  /** The variant's weighted moving average — what it cost last time (§18.1-2). */
  readonly costCents: number;
  /** The store's order for the size, so a summary grid reads 2, 4, 6. */
  readonly sizeSort?: number;
  /** Its place among the style's sizes, so lengths and colors keep the style's order. */
  readonly rank?: number;
  readonly targetQty: number | null;
  readonly onHand: number;
  readonly onOrder: number;
}

export interface DraftLine {
  readonly variantId: string;
  readonly productId: string;
  readonly description: string;
  readonly sku: string;
  readonly quantity: number;
  readonly unitCostCents: number;
  /** The style and the cell, so "On this order" is drawn as grids (2026-10-02). */
  readonly productName: string;
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  readonly altSize: string | null;
  readonly sizeSort?: number;
  readonly rank?: number;
}

export interface OrderDraft {
  readonly vendorId: string | null;
  readonly vendorReference: string;
  readonly season: string;
  readonly shipDate: string;
  /** The vendor's own cut-off: after this the shop does not want the goods (FB-1069). */
  readonly cancelDate: string;
  /** Payment terms as words, "Net 30"; blank takes the vendor's own (FB-1066). */
  readonly terms: string;
  /** An early-payment discount as words; printed, never applied to costs. */
  readonly discountTerms: string;
  readonly notes: string;
  readonly lines: readonly DraftLine[];
  /**
   * Operator interactions spent so far, for the step budget (§2.2).
   *
   * Counted here rather than estimated in a design review, exactly as the
   * import wizard counts its own. System events — a search returning, a style
   * loading — are not interactions and never increment it.
   */
  readonly interactions: number;
}

export const emptyOrderDraft: OrderDraft = {
  vendorId: null,
  vendorReference: '',
  season: '',
  shipDate: '',
  cancelDate: '',
  terms: '',
  discountTerms: '',
  notes: '',
  lines: [],
  interactions: 0,
};

export type OrderDraftEvent =
  | { readonly type: 'vendor-chosen'; readonly vendorId: string }
  | {
      readonly type: 'detail-set';
      readonly field:
        | 'vendorReference'
        | 'season'
        | 'shipDate'
        | 'cancelDate'
        | 'terms'
        | 'discountTerms'
        | 'notes';
      readonly value: string;
    }
  | { readonly type: 'quantity-set'; readonly variant: OrderableVariant; readonly quantity: number }
  | { readonly type: 'cost-set'; readonly variantId: string; readonly unitCostCents: number }
  /** One price for every size of a style on the order — how most vendors quote. */
  | { readonly type: 'style-cost-set'; readonly productId: string; readonly unitCostCents: number }
  | { readonly type: 'style-removed'; readonly productId: string }
  | { readonly type: 'style-filled'; readonly variants: readonly OrderableVariant[] }
  | { readonly type: 'line-removed'; readonly variantId: string }
  | { readonly type: 'reset' };

/**
 * Re-exported rather than reimplemented: this *was* the fourth copy of the
 * rule, and `@sitzmark/core` now owns it. Kept as a named export here so the
 * screens that already import it from this module do not all have to change.
 */
export { describeVariant };

/**
 * What to order to get back to the target (§6.5's formula, by hand).
 *
 * `target − (on hand + on order)`, floored at zero — the same arithmetic the
 * Phase 2 auto-PO will use, offered here as a suggestion a buyer accepts or
 * types over rather than as something that happens to them. Netting off what is
 * already inbound is what stops a second order for goods already coming
 * (decision 7).
 */
export function suggestedQuantity(variant: OrderableVariant): number {
  if (variant.targetQty === null) return 0;
  return Math.max(variant.targetQty - (variant.onHand + variant.onOrder), 0);
}

/** Where a line sits in its style's grid. */
function cellOf(variant: OrderableVariant) {
  return {
    productName: variant.productName,
    colorName: variant.colorName,
    sizeLabel: variant.sizeLabel,
    altSize: variant.altSize ?? null,
    ...(variant.sizeSort === undefined ? {} : { sizeSort: variant.sizeSort }),
    ...(variant.rank === undefined ? {} : { rank: variant.rank }),
  };
}

/** One style on the order: its lines, units and cost, in the order styles were added. */
export interface OrderStyle {
  readonly productId: string;
  readonly productName: string;
  readonly lines: readonly DraftLine[];
  readonly units: number;
  readonly costCents: number;
}

/**
 * "On this order", style by style (2026-10-02): "below the grid where it says
 * on this order … it shows items in a list instead of in a grid." Each style
 * is drawn as the grid it was ordered in, with its cost beside it.
 */
export function orderStyles(lines: readonly DraftLine[]): OrderStyle[] {
  const styles = new Map<string, DraftLine[]>();
  for (const line of lines) {
    const own = styles.get(line.productId) ?? [];
    own.push(line);
    styles.set(line.productId, own);
  }
  return [...styles.entries()].map(([productId, own]) => ({
    productId,
    productName: own[0]!.productName,
    // The style's own order — its lengths and colors as its grid lists them —
    // not the order the boxes were typed in (2026-10-02).
    lines: [...own].sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)),
    units: own.reduce((sum, line) => sum + line.quantity, 0),
    costCents: own.reduce((sum, line) => sum + line.quantity * line.unitCostCents, 0),
  }));
}

function replaceLine(
  lines: readonly DraftLine[],
  variantId: string,
  next: DraftLine | null,
): DraftLine[] {
  if (next === null) return lines.filter((line) => line.variantId !== variantId);
  // In its place: changing a quantity must not move a style down the order.
  const at = lines.findIndex((line) => line.variantId === variantId);
  return at < 0 ? [...lines, next] : lines.map((line, index) => (index === at ? next : line));
}

export function orderDraftReducer(state: OrderDraft, event: OrderDraftEvent): OrderDraft {
  switch (event.type) {
    case 'vendor-chosen':
      // Changing vendor mid-order clears the lines rather than quietly ordering
      // one supplier's goods from another: a purchase order belongs to a
      // vendor, and the alternative fails at the far end when the wrong company
      // is asked for a style they have never heard of.
      return state.vendorId === event.vendorId
        ? state
        : {
            ...state,
            vendorId: event.vendorId,
            lines: [],
            interactions: state.interactions + 1,
          };

    case 'detail-set':
      return { ...state, [event.field]: event.value, interactions: state.interactions + 1 };

    case 'quantity-set': {
      // Zero removes the line. A line of nothing is not an order for nothing —
      // it is a line somebody cleared, and leaving it invites a purchase order
      // full of zeros that reads as though it was placed.
      if (event.quantity <= 0) {
        return {
          ...state,
          lines: replaceLine(state.lines, event.variant.variantId, null),
          interactions: state.interactions + 1,
        };
      }

      const existing = state.lines.find((line) => line.variantId === event.variant.variantId);

      return {
        ...state,
        lines: replaceLine(state.lines, event.variant.variantId, {
          variantId: event.variant.variantId,
          productId: event.variant.productId,
          description: describeVariant(event.variant),
          sku: event.variant.sku,
          ...cellOf(event.variant),
          quantity: event.quantity,
          // Keeps a cost the buyer already typed over; otherwise last cost paid.
          unitCostCents: existing?.unitCostCents ?? event.variant.costCents,
        }),
        interactions: state.interactions + 1,
      };
    }

    case 'cost-set':
      return {
        ...state,
        lines: state.lines.map((line) =>
          line.variantId === event.variantId
            ? { ...line, unitCostCents: event.unitCostCents }
            : line,
        ),
        interactions: state.interactions + 1,
      };

    case 'style-cost-set':
      return {
        ...state,
        lines: state.lines.map((line) =>
          line.productId === event.productId
            ? { ...line, unitCostCents: event.unitCostCents }
            : line,
        ),
        interactions: state.interactions + 1,
      };

    case 'style-removed':
      return {
        ...state,
        lines: state.lines.filter((line) => line.productId !== event.productId),
        interactions: state.interactions + 1,
      };

    case 'style-filled': {
      // One press per style, not one per size — a buyer replenishing a run of
      // eleven sizes should not pay eleven interactions for an arithmetic the
      // system already knows (§2.2-5).
      let lines = [...state.lines];
      for (const variant of event.variants) {
        const quantity = suggestedQuantity(variant);
        const existing = lines.find((line) => line.variantId === variant.variantId);
        lines = replaceLine(
          lines,
          variant.variantId,
          quantity <= 0
            ? null
            : {
                variantId: variant.variantId,
                productId: variant.productId,
                description: describeVariant(variant),
                sku: variant.sku,
                ...cellOf(variant),
                quantity,
                unitCostCents: existing?.unitCostCents ?? variant.costCents,
              },
        );
      }
      return { ...state, lines, interactions: state.interactions + 1 };
    }

    case 'line-removed':
      return {
        ...state,
        lines: replaceLine(state.lines, event.variantId, null),
        interactions: state.interactions + 1,
      };

    case 'reset':
      return emptyOrderDraft;

    default: {
      const never: never = event;
      return never;
    }
  }
}

export interface OrderTotals {
  readonly lines: number;
  readonly units: number;
  readonly costCents: number;
}

export function orderTotals(draft: OrderDraft): OrderTotals {
  return {
    lines: draft.lines.length,
    units: draft.lines.reduce((sum, line) => sum + line.quantity, 0),
    costCents: draft.lines.reduce((sum, line) => sum + line.quantity * line.unitCostCents, 0),
  };
}

/**
 * Why the order cannot be created yet, in the buyer's words.
 *
 * Returned rather than thrown, because it goes on the disabled button: §2.2-7
 * wants an illegal action disabled *with its reason visible*, not a refusal
 * after the press.
 */
export function createBlockedReason(draft: OrderDraft): string | null {
  if (draft.vendorId === null) return 'Choose who you are ordering from';
  if (draft.lines.length === 0) return 'Add at least one item';
  if (draft.lines.some((line) => line.quantity < 1)) return 'Every line needs a quantity';
  return null;
}

/** The payload the server takes — the draft with its screen-only fields dropped. */
export function toCreateInput(draft: OrderDraft): {
  vendorId: string;
  vendorReference: string | null;
  season: string | null;
  shipDate: string | null;
  cancelDate: string | null;
  terms: string | null;
  discountTerms: string | null;
  notes: string | null;
  lines: { variantId: string; quantity: number; unitCostCents: number }[];
} {
  const words = (value: string) => (value.trim() === '' ? null : value.trim());
  return {
    vendorId: draft.vendorId!,
    vendorReference: draft.vendorReference.trim() === '' ? null : draft.vendorReference.trim(),
    season: draft.season.trim() === '' ? null : draft.season.trim(),
    shipDate: draft.shipDate === '' ? null : draft.shipDate,
    cancelDate: draft.cancelDate === '' ? null : draft.cancelDate,
    terms: words(draft.terms),
    discountTerms: words(draft.discountTerms),
    notes: words(draft.notes),
    lines: draft.lines.map((line) => ({
      variantId: line.variantId,
      quantity: line.quantity,
      unitCostCents: line.unitCostCents,
    })),
  };
}
